import React, { useEffect, useState } from 'react';
import { ShoppingBag, Shield, Check, Lock } from 'lucide-react';

/**
 * Onboarding copy of the Shopping Break phone demo (value carousel, screen 3).
 *
 * Deliberately FORKED from `components/pause/ShoppingBreakDemo` so tuning the
 * onboarding version never touches the pause-page asset. Differences from the
 * source: a wider phone, a LIGHT shield screen, and simplified content.
 */

type Phase = 'duration' | 'app1' | 'app2' | 'start' | 'shield';

// The setup selections play out in sequence: pick a window → check each app →
// press Start → the shield takes over.
const SEQUENCE: Array<{ phase: Phase; ms: number }> = [
  { phase: 'duration', ms: 900 },
  { phase: 'app1', ms: 800 },
  { phase: 'app2', ms: 800 },
  { phase: 'start', ms: 1500 },
  { phase: 'shield', ms: 3000 },
];

/** Phone geometry — bigger than the pause demo; full frame (no crop). */
const PHONE_W = 214;
const PHONE_H = 272; // p-1.5 ×2 (12) + inner h-[260px]
const CROP = 1; // show the whole phone

function usePhaseLoop(): Phase {
  const [phase, setPhase] = useState<Phase>('duration');

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPhase('shield');
      return;
    }
    let index = 0;
    let timer: ReturnType<typeof setTimeout>;
    const advance = () => {
      setPhase(SEQUENCE[index].phase);
      timer = setTimeout(() => {
        index = (index + 1) % SEQUENCE.length;
        advance();
      }, SEQUENCE[index].ms);
    };
    advance();
    return () => clearTimeout(timer);
  }, []);

  return phase;
}

/** Soft foliage halo drawn on the control the sequence is "selecting" now. */
const HALO = 'shadow-[0_0_0_3px_rgba(197,160,89,0.35)]';

const PhoneScreens: React.FC<{ phase: Phase }> = ({ phase }) => {
  const onShield = phase === 'shield';
  const order: Phase[] = ['duration', 'app1', 'app2', 'start'];
  // A control is "done" once the sequence has reached its step (stays selected
  // after), and "active" only on its own step (gets the halo).
  const done = (s: Phase) => onShield || order.indexOf(phase) >= order.indexOf(s);
  const active = (s: Phase) => phase === s;

  return (
    <div className="relative rounded-[2rem] bg-growth p-1.5 shadow-lg" style={{ width: PHONE_W }}>
      <div className="relative h-[260px] overflow-hidden rounded-[1.7rem] bg-cloud">
        {/* ── Setup screen — selections light up in sequence ── */}
        <div className={`absolute inset-0 flex flex-col px-3 pb-3 pt-3 transition-opacity duration-300 ${onShield ? 'opacity-0' : 'opacity-100'}`}>
          <div className="flex items-center justify-center gap-1.5">
            <div className="text-[10px] align-middle font-semibold text-growth">Shopping Break</div>
          </div>

          <p className="mt-2.5 text-[8px] font-semibold uppercase tracking-wider text-growth/70">How long?</p>
          <div className="mt-1.5 grid grid-cols-3 gap-1">
            {['Weekend', '24h', '48h'].map((label, i) => {
              const selected = i === 0 && done('duration');
              return (
                <div
                  key={label}
                  className={`rounded-lg border px-1 py-1 text-center text-[9px] font-semibold transition-all duration-300 ${
                    selected ? 'border-foliage bg-foliage/10 text-growth' : 'border-mist text-growth/70'
                  } ${i === 0 && active('duration') ? HALO : ''}`}
                >
                  {label}
                </div>
              );
            })}
          </div>

          <p className="mt-2.5 text-[8px] font-semibold uppercase tracking-wider text-growth/70">Apps to block</p>
          <div className="mt-1.5 space-y-1.5">
            {(['app1', 'app2'] as const).map((key, i) => {
              const checked = done(key);
              return (
                <div
                  key={key}
                  className={`flex items-center gap-2 rounded-lg border border-mist px-2 py-1 transition-all duration-300 ${
                    active(key) ? HALO : ''
                  }`}
                >
                  <div className={`h-5 w-5 rounded-md ${i === 0 ? 'bg-clay/20' : 'bg-sky/20'} flex items-center justify-center`}>
                    <ShoppingBag className="h-3 w-3 text-growth/60" />
                  </div>
                  <div className="h-1.5 w-16 rounded-full bg-mist" />
                  <div
                    className={`ml-auto flex h-3.5 w-3.5 items-center justify-center rounded-full transition-colors duration-300 ${
                      checked ? 'bg-foliage' : 'border border-mist'
                    }`}
                  >
                    {checked && <Check className="h-2.5 w-2.5 text-cloud" />}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Start — a brief pressed state on its step, then the shield takes over. */}
          <div
            className={`mt-auto flex h-9 items-center justify-center rounded-xl bg-foliage transition-all duration-300 ${
              active('start') ? 'scale-95 brightness-95' : ''
            }`}
          >
            <span className="text-[10px] font-semibold text-cloud">Start your Shopping Break</span>
          </div>
        </div>

        {/* ── enough shield — LIGHT screen ── */}
        <div className={`absolute inset-0 flex flex-col items-center justify-center bg-sand px-4 text-center transition-opacity duration-300 ${onShield ? 'opacity-100' : 'opacity-0'}`}>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-growth/10">
            <Lock className="h-5 w-5 text-growth" />
          </div>
          <p className="font-serif text-[13px] text-growth">Quiet for now</p>
          <p className="mt-1 text-[9px] leading-relaxed text-growth/60">
            These apps are paused until Sunday night.
          </p>
          <div className="mt-3 rounded-full bg-growth px-3.5 py-1.5">
            <span className="text-[9px] font-semibold text-cloud">Go back</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * The looping demo. Mount only when visible — the phase loop timer runs for
 * the life of the component.
 */
export const ShoppingBreakPhone: React.FC = () => {
  const phase = usePhaseLoop();
  return (
    <div className="mx-auto overflow-hidden" style={{ width: PHONE_W, height: Math.round(PHONE_H * CROP) }}>
      <PhoneScreens phase={phase} />
    </div>
  );
};
